<script setup lang="ts">
import { nextTick, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ApiRequestError, login } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { safeReturnPath } from '@/stores/authNavigation'
import { LoginSchema, firstIssue } from '@/validation'
import { maxLength, tidy } from '@/lib/inputFormat'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const email = ref('')
const password = ref('')
const busy = ref(false)
const error = ref('')
const emailField = ref<HTMLInputElement | null>(null)

function tidyEmail() {
  email.value = tidy('email', email.value) as string
}

async function submit() {
  error.value = ''
  const issue = firstIssue(LoginSchema.safeParse({ email: email.value, password: password.value }))
  if (issue !== undefined) {
    error.value = issue
    return
  }
  busy.value = true
  try {
    const account = await login(email.value, password.value)
    auth.setAccount(account)
    await router.replace(
      safeReturnPath(route.query.return) ?? (auth.isAdmin ? '/admin/intakes' : '/account'),
    )
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError ? cause.message : 'Unable to sign in. Please try again.'
    // Re-enable the form before focusing: the field is bound to `busy`, and a
    // disabled input silently refuses focus.
    busy.value = false
    await nextTick()
    emailField.value?.focus()
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <main class="auth-page">
    <section class="auth-card" aria-labelledby="login-title">
      <p class="eyebrow">Castle Country Digital</p>
      <h1 id="login-title" class="display">Sign in</h1>
      <p class="auth-lede">Use your account to manage intake links.</p>
      <p v-if="route.query.session === 'expired'" class="form-notice" role="status">
        Your session expired. Please sign in again.
      </p>
      <p v-else-if="route.query.session === 'unavailable'" class="form-error" role="alert">
        We could not check your session. Please sign in again.
      </p>
      <form novalidate @submit.prevent="submit">
        <label
          >Email
          <input
            ref="emailField"
            v-model="email"
            type="email"
            autocomplete="email"
            autocapitalize="off"
            spellcheck="false"
            enterkeyhint="next"
            :maxlength="maxLength.text"
            required
            :disabled="busy"
            @blur="tidyEmail"
        /></label>
        <label
          >Password
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            required
            :disabled="busy"
        /></label>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="btn btn-primary" type="submit" :disabled="busy">
          {{ busy ? 'Signing in…' : 'Sign in' }}
        </button>
      </form>
      <p class="auth-switch">
        Need an account? <RouterLink to="/register">Create account</RouterLink>
      </p>
    </section>
  </main>
</template>
